import React, { useState, useEffect } from "react";
import {
  Box,
  Flex,
  Image,
  Text,
  Button,
  Grid,
  GridItem,
  Icon,
  IconButton,
  useToast,
  useClipboard,
  Tooltip,
} from "@chakra-ui/react";
import { BiMenu } from "react-icons/bi";
import { useMainV2Logic } from "../Logic/useMainV2Logic";
import { shouldShowLockButton } from "../../Helper/guestAppConfig";
import { RiDoorLockBoxFill, RiLockPasswordFill } from "react-icons/ri";
import { AiTwotoneLock, AiTwotoneUnlock } from "react-icons/ai";
import { FaDoorOpen } from "react-icons/fa";
import { CircularProgress, Flex as ChakraFlex, keyframes } from "@chakra-ui/react";
import { MdWifi } from "react-icons/md";
import { ImEnter, ImExit } from "react-icons/im";
import { FiCopy } from "react-icons/fi";
import { GuidesSection } from "./Sections/GuidesSection";
import { HubNavigation } from "./Components/HubNavigation";
import { ServicesSection } from "./Sections/ServicesSection";
import { ViatorSection } from "./Components/ViatorSection";
import { PROPERTY_CATALOG_ID } from "./Logic/propertyGuideItems";
import { useNavigate } from "react-router-dom";
import { CheckInComponent } from "../../UI/Mini/checkInComponent";
import { isCheckOutDay } from "../Logic/checkoutDay";
import { useTemperatureUnit, formatTemperature } from "../Logic/temperatureUnit";
import { FooterComponent } from "./Components/FooterComponent";
import { MenuDisplay } from "../../UI/Mini/menu";
import { LobbyCodeSection } from "./LobbyCodeSection";
import { useLobbyCode } from "../Logic/useMainV2Logic";
import { ViatorModal } from "../../UI/Mini/viatorModal";
import { CiboModal } from "../../UI/Mini/ciboModal";

const doorPulse = keyframes`
  0%, 100% { opacity: 0.4; transform: scale(0.96); }
  50%      { opacity: 1;   transform: scale(1); }
`;

// On md+ each section sits in its own panel so the desktop layout reads as
// a dashboard instead of a stretched mobile column. On base it renders as a
// plain box, leaving the mobile design untouched.
const SectionCard = ({ children, ...rest }) => (
  <Box
    bg={{ base: "transparent", md: "rgba(255, 255, 255, 0.03)" }}
    border={{ md: "1px solid rgba(255, 255, 255, 0.08)" }}
    borderRadius={{ md: "20px" }}
    p={{ md: 5 }}
    {...rest}
  >
    {children}
  </Box>
);

// Wifi values share a half-width column with the copy button, so a long
// SSID or passphrase at the default 16px wraps into a stack of four-character
// fragments. Step the size down with length to keep long values legible
// while short ones keep the same weight as the times beside them.
const wifiValueFontSize = (value) => {
  const length = String(value ?? "").length;
  if (length > 28) return "12px";
  if (length > 18) return "13px";
  return "16px";
};

export const MainV2 = () => {
  const navigate = useNavigate();
  const [isGuideDrawerOpen, setIsGuideDrawerOpen] = useState(false);
  const [isServiceDrawerOpen, setIsServiceDrawerOpen] = useState(false);
  const [isViatorOpen, setIsViatorOpen] = useState(false);
  const [isFormsDrawerOpen, setIsFormsDrawerOpen] = useState(false);
  const [isHubNavOpen, setIsHubNavOpen] = useState(false);
  const [isHubNavVisible, setIsHubNavVisible] = useState(false);
  
  const {
    room,
    imageUrl,
    getAddressText,
    booking,
    accessTimes,
    doorState,
    doorLoading,
    handleDoor,
    isDoorDisabled,
    doorError,
    clearDoorError,
    weather,
    weatherLoading,
    manager,
    isMenuOpen,
    handleOpenMenu,
    handleCloseMenu,
    // Cibo
    isCiboOpen,
    handleOpenCibo,
    handleCloseCibo,
    getProcessedCiboLink,
    isLobbyDisabled,
  } = useMainV2Logic();

  const { unit: tempUnit, toggle: toggleTempUnit } = useTemperatureUnit(room, weather);

    const toast = useToast();
  const { hasCopied, onCopy } = useClipboard(room?.wifiPass || "");

  // Host setting from the same booking-details response that gates this
  // screen's initial render, so it's known before first paint — the lock
  // button is never rendered and then removed. Missing flag = show.
  const showLockButton = shouldShowLockButton(booking);

  const effectiveCheckInTime = accessTimes?.effectiveCheckInTime || room?.checkInTime;
  const effectiveCheckOutTime = accessTimes?.effectiveCheckOutTime || room?.checkOutTime;

  // Adjustment detection is intentionally forgiving. The BE has had
  // more than one shape ship in prod for approved early check-ins /
  // late check-outs — sometimes `adjustments: [{ kind: "EARLY_CHECK_IN", ... }]`,
  // sometimes with `type` instead of `kind`, sometimes with a
  // different casing (`earlyCheckIn`, `EARLY-CHECK-IN`, etc.), and in
  // some cases nothing structured at all — only the `effective*Time`
  // gets bumped. Guests with a legitimately approved early check-in
  // were being shown no badge because our strict `=== "EARLY_CHECK_IN"`
  // match rejected everything else, so we now:
  //   1. Normalize `kind`/`type` before comparing.
  //   2. Fall back to inferring the adjustment from the effective
  //      vs. room time delta so approved changes always render even
  //      when the BE forgets to emit the structured entry.
  const normKind = (s) => String(s ?? "").toUpperCase().replace(/[^A-Z]/g, "");
  const parseHHMM = (t) => {
    if (!t || typeof t !== "string") return null;
    const [h, m] = t.split(":").map(Number);
    if (Number.isNaN(h) || Number.isNaN(m)) return null;
    return h * 60 + m;
  };
  const diffHours = (a, b) => {
    const av = parseHHMM(a);
    const bv = parseHHMM(b);
    if (av == null || bv == null) return null;
    return Math.round(Math.abs(av - bv) / 60);
  };

  let checkInAdjustment = accessTimes?.adjustments?.find(
    (a) => normKind(a?.kind ?? a?.type) === "EARLYCHECKIN"
  );
  let checkOutAdjustment = accessTimes?.adjustments?.find(
    (a) => normKind(a?.kind ?? a?.type) === "LATECHECKOUT"
  );

  if (!checkInAdjustment) {
    const roomIn = parseHHMM(accessTimes?.roomCheckInTime);
    const effIn = parseHHMM(accessTimes?.effectiveCheckInTime);
    if (roomIn != null && effIn != null && effIn < roomIn) {
      checkInAdjustment = {
        kind: "EARLY_CHECK_IN",
        hours: diffHours(accessTimes?.roomCheckInTime, accessTimes?.effectiveCheckInTime),
        note: "Early check-in confirmed",
      };
    }
  }

  if (!checkOutAdjustment) {
    const roomOut = parseHHMM(accessTimes?.roomCheckOutTime);
    const effOut = parseHHMM(accessTimes?.effectiveCheckOutTime);
    if (roomOut != null && effOut != null && effOut > roomOut) {
      checkOutAdjustment = {
        kind: "LATE_CHECK_OUT",
        hours: diffHours(accessTimes?.roomCheckOutTime, accessTimes?.effectiveCheckOutTime),
        note: "Late check-out confirmed",
      };
    }
  }

  // Lobby code state
  const { lobbyCode, lobbyCodeLoading, lobbyCodeError } = useLobbyCode(room, booking);

  const handleLobbyAccess = () => {
    handleOpenCibo();
  };

  // Property info and guides are one destination. Every entry point (the
  // floating bar's "Info", the menu) lands here.
  const handleOpenInfoHub = () => {
    navigate("/guides", {
      state: { room, imageUrl, initialCatalog: PROPERTY_CATALOG_ID },
    });
  };

  const handleCopy = () => {
    onCopy();
    toast({
      title: "Copied to clipboard",
      description: "Wi-Fi password has been copied.",
      status: "success",
      duration: 3000,
      isClosable: true,
    });
  };

  useEffect(() => {
    if (!doorError) return;

    const title =
      doorError.kind === "window"
        ? "Door access not available yet"
        : "Couldn't reach the lock";

    toast({
      title,
      description: doorError.message,
      status: doorError.kind === "window" ? "warning" : "error",
      duration: 6000,
      isClosable: true,
      position: "top",
    });

    clearDoorError();
  }, [doorError, toast, clearDoorError]);

  const convertTimeTo12HourFormat = (timeString) => {
    if (!timeString) return;
    const [hours, minutes] = timeString.split(":");
    let formattedTime = "";

    if (hours < 12) {
      formattedTime = `${hours}:${minutes} AM`;
    } else if (hours === "12") {
      formattedTime = `${hours}:${minutes} PM`;
    } else {
      formattedTime = `${hours - 12}:${minutes} PM`;
    }

    return formattedTime;
  };

  // Door Control Button
  const DoorControlButton = () => {
    const doorDisabled = isDoorDisabled();
    const isLocked = doorState;

    return (
      <Tooltip
        label={doorDisabled ? "Available during your stay" : ""}
        fontSize="12px"
        fontWeight={600}
        letterSpacing="0.01em"
        bg="#FBBF24"
        color="#111827"
        border="1px solid rgba(255, 255, 255, 0.22)"
        borderRadius="12px"
        px="12px"
        py="8px"
        boxShadow="0 12px 30px rgba(251, 191, 36, 0.28)"
        placement="bottom"
        isOpen={!isGuideDrawerOpen && !isServiceDrawerOpen && !isCiboOpen && !isFormsDrawerOpen && !isHubNavOpen && doorDisabled}
        hasArrow
      >
        <Button
          onClick={handleDoor}
          isDisabled={doorLoading || doorDisabled}
          bg={
            isLocked
              ? "rgba(16, 185, 129, 0.10)"
              : "rgba(239, 68, 68, 0.10)"
          }
          border="1px solid"
          borderColor={
            isLocked
              ? "rgba(16, 185, 129, 0.32)"
              : "rgba(239, 68, 68, 0.34)"
          }
          color={isLocked ? "#34D399" : "#F87171"}
          h="44px"
          w="full"
          variant="solid"
          borderRadius="10px"
          fontSize="14px"
          fontWeight={600}
          letterSpacing="0.01em"
          _hover={{
            bg: isLocked
              ? "rgba(16, 185, 129, 0.16)"
              : "rgba(239, 68, 68, 0.16)",
            borderColor: isLocked
              ? "rgba(16, 185, 129, 0.5)"
              : "rgba(239, 68, 68, 0.52)",
          }}
          _active={{
            bg: isLocked
              ? "rgba(16, 185, 129, 0.22)"
              : "rgba(239, 68, 68, 0.22)",
          }}
          _disabled={{
            opacity: doorLoading ? 1 : 0.45,
            cursor: doorLoading ? "progress" : "not-allowed",
            bg: isLocked
              ? "rgba(16, 185, 129, 0.10)"
              : "rgba(239, 68, 68, 0.10)",
            borderColor: isLocked
              ? "rgba(16, 185, 129, 0.32)"
              : "rgba(239, 68, 68, 0.34)",
            color: isLocked ? "#34D399" : "#F87171",
          }}
          transition="background 0.18s ease, border-color 0.18s ease, color 0.18s ease"
        >
          <ChakraFlex align="center" justify="center" gap={2}>
            <Icon
              as={isLocked ? AiTwotoneLock : AiTwotoneUnlock}
              boxSize={4}
              animation={
                doorLoading
                  ? `${doorPulse} 1.1s ease-in-out infinite`
                  : undefined
              }
              transformOrigin="center"
            />
            <Text>
              {doorLoading
                ? isLocked
                  ? "Unlocking\u2026"
                  : "Locking\u2026"
                : isLocked
                ? "Locked"
                : "Unlocked"}
            </Text>
          </ChakraFlex>
        </Button>
      </Tooltip>
    );
  };

  // The redirect effects above handle where to send the user; this guard
  // decides what paints in the meantime. Without it, the full main screen —
  // door code included — rendered for the frames before the redirect fired,
  // which is enough to read (or screenshot) with security steps unfinished.
  if (!room || !booking || booking.securityStepsComplete !== true) {
    return (
      <Flex h="100vh" alignItems="center" justifyContent="center">
        <CircularProgress
          isIndeterminate
          color="#817EFF"
          size="40px"
          trackColor="transparent"
        />
      </Flex>
    );
  }

  return (
    <Box>
      <Box position="relative">
        <Image
          src={imageUrl}
          h={{ base: "280px", md: "380px", lg: "440px" }}
          w={"full"}
          objectFit={"cover"}
        />

        <Box
          position="absolute"
          top="2px"
          left="0"
          width="100%"
          height="100%"
          background="linear-gradient(180deg, rgb(0 0 0 / 0%) 0%, #1A202C 100%), transparent"
        />

        <Flex w="100%" top={5} position="absolute" px={{ base: 2, md: 0 }} justifyContent="flex-start">
          <Box w="100%" maxW="1200px" mx="auto" px={{ md: 8 }}>
            {(weatherLoading || weather?.current?.temp_c != null) && (
              <Box
                as="button"
                type="button"
                // Tap to switch °F / °C; the choice is remembered.
                onClick={weatherLoading ? undefined : toggleTempUnit}
                aria-label={`Temperature in ${tempUnit === "F" ? "Fahrenheit" : "Celsius"}, tap to switch`}
                cursor={weatherLoading ? "default" : "pointer"}
                _active={{ transform: "scale(0.96)" }}
                transition="transform 0.1s ease"
                bg="rgba(0, 0, 0, 0.6)"
                color="white"
                px={3}
                py={2}
                borderRadius="15px"
                display="inline-flex"
                alignItems="center"
                gap={2}
                backdropFilter="blur(10px)"
              >
                {weatherLoading ? (
                  <CircularProgress size="20px" isIndeterminate color="white" />
                ) : weather?.current?.condition?.icon ? (
                  <Image 
                    src={`https:${weather.current.condition.icon}`} 
                    alt="weather" 
                    w="24px" 
                    h="24px" 
                    objectFit="contain"
                  />
                ) : null}
                <Text fontSize="14px" fontWeight="600">
                  {weatherLoading ? "Loading..." : formatTemperature(weather?.current, tempUnit)}
                </Text>
                {!weatherLoading && (
                  <Text fontSize="11px" fontWeight="700" color="whiteAlpha.600">
                    °{tempUnit === "F" ? "C" : "F"}
                  </Text>
                )}
              </Box>
            )}
          </Box>
        </Flex>

        {/* Address, anchored to the hero's bottom edge where the gradient fades
            into the page. Actions live in the floating bar, so the hero is
            just the place and the photo. */}
        <Flex
          position="absolute"
          bottom={0}
          left={0}
          w="100%"
        >
          <Box
            w="100%"
            maxW="1200px"
            mx="auto"
            px={{ base: 2, md: 8 }}
            pb={{ base: 3, md: 4 }}
          >
            <Text
              fontSize={{ base: "18px", md: "24px", lg: "28px" }}
              fontWeight={800}
              noOfLines={2}
              lineHeight={{ base: "1.35", md: "1.4" }}
              maxW={{ md: "70%" }}
            >
              {getAddressText()}
            </Text>
          </Box>
        </Flex>
      </Box>
      
      <Box maxW="1200px" mx="auto" px={{ base: 0, md: 8 }} pt={{ base: 3, md: 6 }}>
        <Flex
          direction={{ base: "column", lg: "row" }}
          align={{ lg: "flex-start" }}
          gap={{ lg: 8 }}
        >
          {/* Access & stay info — top of page on mobile, right sidebar on desktop */}
          <Flex
            direction="column"
            order={{ base: 1, lg: 2 }}
            w={{ base: "100%", lg: "380px" }}
            flexShrink={0}
            gap={{ md: 5 }}
          >
      {/* Door Access Area */}
      {booking?.deviceID && (
        <SectionCard px={{ base: 4, md: 5 }} py={{ base: 2, md: 5 }}>
          <Text fontSize={"12px"} fontWeight={800} color={"white"} mb={1}>
            Door Access
          </Text>
          <Grid
            templateColumns={showLockButton ? "repeat(2, 1fr)" : "1fr"}
            w={"full"}
            gap={6}
            pt={2}
          >
            {showLockButton && (
              <GridItem>
                <Text fontSize={"12px"} color={"#D0D0D0"} fontWeight={600} mb={"6px"}>
                  Door Control
                </Text>
                <Flex alignItems={"start"}>
                  <DoorControlButton />
                </Flex>
              </GridItem>
            )}
            <GridItem>
              <Icon as={RiDoorLockBoxFill} mr="2" fontSize={"20px"} />
              <Text fontSize={"12px"} color={"#D0D0D0"} fontWeight={600}>
                Smart Lock Code
              </Text>
              <Text fontSize={"16px"} fontWeight={800}>
                {booking.code ? booking.code : "No code available"}
              </Text>
            </GridItem>
          </Grid>
          {/* Lobby Code Area */}
          <LobbyCodeSection lobbyCode={lobbyCode} lobbyCodeLoading={lobbyCodeLoading} lobbyCodeError={lobbyCodeError} show={room?.ciboCode} />
        </SectionCard>
      )}

      {/* Lobby Access Area */}
      {room?.ciboLink && (
        <SectionCard px={{ base: 4, md: 5 }} py={{ base: 2, md: 5 }}>
          <Grid templateColumns="1fr" w={"full"} gap={6}>
            <GridItem>
              <Flex alignItems={"start"}>
                <Button
                  onClick={handleLobbyAccess}
                  bg={"#817efc"}
                  color="white"
                  h="42px"
                  w="full"
                  variant="solid"
                  borderRadius="10px"
                  fontSize="14px"
                  leftIcon={<FaDoorOpen />}
                >
                  Open Lobby
                </Button>
              </Flex>
            </GridItem>
          </Grid>
        </SectionCard>
      )}

      {/* Check In Component — on the booking's last day (property's clock)
          it renders as Check Out instead, so the card also shows for rooms
          that only have check-out steps once that day arrives. */}
      {room &&
        ((Array.isArray(room.checkIn) && room.checkIn.length > 0) ||
          (isCheckOutDay(booking, room) &&
            Array.isArray(room.checkOut) &&
            room.checkOut.length > 0)) && (
          <SectionCard px={{ base: 4, md: 5 }} pt={{ base: 3, md: 5 }} pb={{ md: 5 }}>
            <CheckInComponent room={room} booking={booking} accessTimes={accessTimes} manager={manager} />
          </SectionCard>
        )}

      {/* Information Section */}
      <SectionCard
        display="flex"
        flexDirection="column"
        px={{ base: 4, md: 5 }}
        pt={{ base: 3, md: 5 }}
        pb={{ base: 2, md: 5 }}
      >
        <Text fontSize={"12px"} fontWeight={800} color={"white"}>
          Information
        </Text>
        <Grid templateColumns="repeat(2, 1fr)" w={"full"} gap={6} pt={4}>
          <GridItem>
            <Icon as={ImEnter} mr="2" fontSize={"20px"} />
            <Text fontSize={"12px"} color={"#D0D0D0"} fontWeight={600}>
              Check In Time
            </Text>
            <Text fontSize={"16px"} fontWeight={800}>
              {room && convertTimeTo12HourFormat(effectiveCheckInTime)}
            </Text>
            {checkInAdjustment && (
              <Tooltip
                label={checkInAdjustment.note || "Early check-in confirmed"}
                placement="top"
                hasArrow
                bg="#2a2a3a"
                color="white"
              >
                <Flex
                  mt={1}
                  display="inline-flex"
                  alignItems="center"
                  gap={1}
                  px={2}
                  py={"2px"}
                  borderRadius="full"
                  bg="rgba(129, 126, 255, 0.18)"
                  border="1px solid"
                  borderColor="rgba(129, 126, 255, 0.45)"
                  cursor="help"
                >
                  <Text fontSize="10px" fontWeight={700} color="#B9B7FF" letterSpacing="0.02em">
                    Early check-in{checkInAdjustment.hours != null ? ` · ${checkInAdjustment.hours}h` : ""}
                  </Text>
                </Flex>
              </Tooltip>
            )}
          </GridItem>
          <GridItem>
            <Icon as={ImExit} mr="2" fontSize={"20px"} />
            <Text fontSize={"12px"} color={"#D0D0D0"} fontWeight={600}>
              Check Out Time
            </Text>
            <Text fontSize={"16px"} fontWeight={800}>
              {room && convertTimeTo12HourFormat(effectiveCheckOutTime)}
            </Text>
            {checkOutAdjustment && (
              <Tooltip
                label={checkOutAdjustment.note || "Late check-out confirmed"}
                placement="top"
                hasArrow
                bg="#2a2a3a"
                color="white"
              >
                <Flex
                  mt={1}
                  display="inline-flex"
                  alignItems="center"
                  gap={1}
                  px={2}
                  py={"2px"}
                  borderRadius="full"
                  bg="rgba(129, 126, 255, 0.18)"
                  border="1px solid"
                  borderColor="rgba(129, 126, 255, 0.45)"
                  cursor="help"
                >
                  <Text fontSize="10px" fontWeight={700} color="#B9B7FF" letterSpacing="0.02em">
                    Late check-out{checkOutAdjustment.hours != null ? ` · ${checkOutAdjustment.hours}h` : ""}
                  </Text>
                </Flex>
              </Tooltip>
            )}
          </GridItem>

          {/* Network names and passwords are host-entered free text and often
              arrive as one long unbreakable token (an SSID like a domain name,
              a run-together passphrase). Grid items default to min-width:auto,
              so without minW={0} such a value widens its column past its 1fr
              share, starving the other column and spilling out of the card. */}
          <GridItem minW={0}>
            <Icon as={MdWifi} mr="2" fontSize={"20px"} />
            <Text fontSize={"12px"} color={"#D0D0D0"} fontWeight={600}>
              Wifi Network
            </Text>
            <Text
              fontSize={wifiValueFontSize(room?.wifiName)}
              fontWeight={800}
              overflowWrap="anywhere"
            >
              {room?.wifiName}
            </Text>
          </GridItem>
          <GridItem minW={0}>
            <Icon as={RiLockPasswordFill} mr="2" fontSize={"20px"} />
            <Text fontSize={"12px"} color={"#D0D0D0"} fontWeight={600}>
              Wifi Password
            </Text>
            <Flex align="center" gap={2}>
              <Text
                fontSize={wifiValueFontSize(room?.wifiPass)}
                fontWeight={800}
                overflowWrap="anywhere"
                whiteSpace="pre-wrap"
                flex="1"
                minW={0}
              >
                {room?.wifiPass}
              </Text>
              {room?.wifiPass && (
                <IconButton
                  icon={<FiCopy />}
                  size="sm"
                  aria-label="Copy WiFi password"
                  onClick={handleCopy}
                  bg="rgb(63, 68, 78)"
                  _hover={{ bg: "rgb(53, 58, 68)" }}
                  color="white"
                  flexShrink={0}
                />
              )}
            </Flex>
          </GridItem>
        </Grid>
      </SectionCard>
          </Flex>

          {/* Services, experiences & guides — main column on desktop */}
          <Box order={{ base: 2, lg: 1 }} flex="1" minW={0} w="100%">
            {/* Services Section */}
            <ServicesSection room={room} booking={booking} onDrawerStateChange={setIsServiceDrawerOpen} />

            <ViatorSection show={!!room?.viatorLink} onOpen={() => setIsViatorOpen(true)} />

            {/* Guides Section */}
            <GuidesSection room={room} imageUrl={imageUrl} onDrawerStateChange={setIsGuideDrawerOpen} />
          </Box>
        </Flex>
      </Box>

      {/* Floating Directions / Map / Info bar, with the drawers it opens. */}
      <HubNavigation
        room={room}
        onOpenInfo={handleOpenInfoHub}
        onOpenChange={setIsHubNavOpen}
        onVisibleChange={setIsHubNavVisible}
        // The footer pads itself clear of the bar, so there's no dead strip
        // between the last section and the footer logo.
        reserveSpace={false}
      />

      {/* Viator Modal */}
      <ViatorModal
        isOpen={isViatorOpen}
        onClose={() => setIsViatorOpen(false)}
        viatorID={room?.viatorID}
        viatorLink={room?.viatorLink}
      />

      {/* Cibo Modal */}
      {room?.ciboLink && (
        <CiboModal
          onClose={handleCloseCibo}
          isOpen={isCiboOpen}
          link={getProcessedCiboLink()}
        />
      )}
      
      {/* Footer */}
      <FooterComponent manager={manager} clearFloatingNav={isHubNavVisible} />
      
      {/* Menu Button */}
      <Button
        leftIcon={<BiMenu />}
        bg={"#817EFF"}
        right={{ base: 4, md: 8 }}
        top={5}
        h={"45px"}
        position="fixed"
        variant="solid"
        borderRadius={"20px"}
        fontSize={"12px"}
        _active={{ bg: "#615edc" }}
        _hover={{ bg: "#615edc" }}
        onClick={handleOpenMenu}
      >
        Menu
      </Button>

      {/* Menu Display */}
      <MenuDisplay
        onClose={handleCloseMenu}
        isOpen={isMenuOpen}
        aboutOpen={handleOpenInfoHub}
        room={room}
        booking={booking}
        accessTimes={accessTimes}
        manager={manager}
        onFormsOpenChange={setIsFormsDrawerOpen}
      />

    </Box>
  );
};
